import type { Dispatch, SetStateAction } from 'react';
import PlayerPicker from './player-picker';
import type { TeamMember } from './types';

type PitchRow = {
  /** Single position for the whole row (starting XI rows). */
  positionId?: number;
  /** Per-slot positions, used by the mixed-position bench row. */
  positionIds?: number[];
  /** Inclusive playerId slot range for this row. */
  range: [number, number];
  /** Row-specific layout on top of the shared `flex items-end`. */
  className: string;
  /** Open the picker dropdown above the slot (avoids scrolling on bottom rows). */
  openUpwards?: boolean;
};

// Slot layout is XI-first then bench, so the array order submitted to
// team-rating is the starting XI (slots 1-11) followed by the bench (12-15).
// Fixed 3-4-3: GK, 3 DEF, 4 MID, 3 FWD. This matches the server's tie-break
// formation (the first entry in the API's VALID_FORMATIONS), so pre-GW1 the
// rated squad it returns lines up with these rows.
export const PITCH_XI_ROWS: PitchRow[] = [
  {
    positionId: 1,
    range: [0, 0],
    className: 'justify-around w-full md:w-80 mx-auto'
  },
  {
    positionId: 2,
    range: [1, 3],
    className: 'justify-around w-full md:w-80 mx-auto'
  },
  {
    positionId: 3,
    range: [4, 7],
    className: 'justify-between'
  },
  {
    positionId: 4,
    range: [8, 10],
    className: 'justify-around w-full md:w-96 mx-auto',
    openUpwards: true
  }
];

// Bench: backup GK + 1 DEF + 1 MID + 1 DEF (the leftover of a 2/5/5/3 squad
// once the 3-4-3 XI is picked). Mixed positions, so each slot has its own
// positionId.
export const PITCH_BENCH_ROW: PitchRow = {
  positionIds: [1, 2, 3, 2],
  range: [11, 14],
  className: 'justify-between',
  openUpwards: true
};

type PlayerRowProps = {
  row: PitchRow;
  team: TeamMember[];
  setTeam: Dispatch<SetStateAction<TeamMember[]>>;
  onDelete: (playerId: number) => void;
};

const PlayerRow = ({ row, team, setTeam, onDelete }: PlayerRowProps) => (
  <div className={`flex items-end ${row.className}`}>
    {team
      .filter(
        ({ playerId }) => playerId >= row.range[0] && playerId <= row.range[1]
      )
      .map(({ playerId }, index) => (
        <PlayerPicker
          key={`player-picker-${playerId}`}
          openUpwards={row.openUpwards}
          playerId={playerId}
          positionId={row.positionId ?? row.positionIds?.[index] ?? 1}
          team={team}
          setTeam={setTeam}
          onCancel={() => onDelete(playerId)}
        />
      ))}
  </div>
);

export default PlayerRow;
